/**
 * List - Simple list component for content
 * Supports both ordered and unordered lists with various styles
 */

import React from 'react'
import { Typography, Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'

interface ListProps {
	items: string[]
	type?: 'unordered' | 'ordered' | 'none'
	marker?: 'bullet' | 'dash' | 'star' | 'number' | 'none'
	color?: 'primary' | 'secondary' | 'white' | 'inherit'
}

const List: React.FC<ListProps> = ({ items, type = 'unordered', marker = 'bullet', color = 'inherit' }) => {
	const theme = useTheme()

	const getColor = () => {
		switch (color) {
			case 'primary':
				return theme.palette.primary.main
			case 'secondary':
				return theme.palette.secondary.main
			case 'white':
				return 'white'
			case 'inherit':
				return 'inherit'
			default:
				return 'inherit'
		}
	}

	const getMarkerStyle = () => {
		if (type === 'ordered') return 'decimal'
		if (marker === 'none' || type === 'none') return 'none'

		switch (marker) {
			case 'bullet':
				return 'disc'
			case 'dash':
				return 'none' // We'll handle this manually
			case 'star':
				return 'none' // We'll handle this manually
			default:
				return 'disc'
		}
	}

	const getMarkerContent = (index: number) => {
		if (type === 'ordered') return `${index + 1}. `
		if (marker === 'dash') return '- '
		if (marker === 'star') return '⭐️ '
		return ''
	}

	const Component = type === 'ordered' ? 'ol' : 'ul'

	return (
		<Box
			component={Component}
			sx={{
				listStyleType: getMarkerStyle(),
				pl: marker === 'dash' || marker === 'star' ? 0 : 2,
				m: 0
			}}
		>
			{items.map((item, index) => (
				<Typography
					key={index}
					component="li"
					variant="body1"
					sx={{
						color: getColor(),
						listStyleType: getMarkerStyle(),
						mb: 1,
						'&::before':
							marker === 'dash' || marker === 'star'
								? {
										content: `"${getMarkerContent(index)}"`,
										marginRight: 1
									}
								: undefined
					}}
				>
					{marker === 'dash' || marker === 'star' ? item : item}
				</Typography>
			))}
		</Box>
	)
}

export default List
